"use client";

import { useLayoutEffect, useMemo, useRef } from "react";
import { NotebookStoreProvider } from "@/lib/notebook/core/NotebookStoreProvider";
import { useNotebook } from "@/lib/notebook/core/store";
import NotebookShell from "@/lib/notebook/editor/shell/NotebookShell";
import { PublicationIdentity } from "@/lib/notebook/editor/shell/PublicNotebookChrome";
import { NotebookPasteInit } from "@/lib/notebook/core/paste-init";
import type { PublicNotebookPayload } from "@/lib/notebook/public/load-public-notebook";
import "@/lib/notebook/public/public-notebook.css";

type PublicNotebookPageClientProps = {
  payload: PublicNotebookPayload;
};

function buildHydrationKey(payload: PublicNotebookPayload): string {
  const hero = payload.sections.find(
    (s) => s.type === "block_hero" || s.type === "hero",
  );
  const heroTitle =
    hero?.data && typeof hero.data === "object" && "title" in hero.data
      ? String((hero.data as { title?: unknown }).title ?? "")
      : "";
  return [
    payload.notebook.id,
    payload.notebook.updated_at ?? "",
    payload.sections.length,
    heroTitle,
  ].join("|");
}

function PublicNotebookInner({ payload }: PublicNotebookPageClientProps) {
  const notebookId = payload.notebook.id;
  const hydrationKey = useMemo(() => buildHydrationKey(payload), [payload]);
  const payloadRef = useRef(payload);
  payloadRef.current = payload;

  const hydrateFromPublic = useNotebook((s) => s.hydrateFromPublic);
  const sessionReady = useNotebook(
    (s) =>
      s.publicMode &&
      s.hydrated &&
      s.notebookId === notebookId &&
      s.contentReadyEpoch === s.sessionEpoch,
  );

  useLayoutEffect(() => {
    hydrateFromPublic(payloadRef.current);
  }, [hydrationKey, hydrateFromPublic]);

  return (
    <div className="nb-public-page nb-root notebook-theme" data-nb-public-page>
      {sessionReady ? (
        <NotebookShell notebookId={notebookId} variant="public" />
      ) : (
        <div className="nb-public-shell nb-public-shell--boot">
          <PublicationIdentity updatedAt={payload.notebook.updated_at} />
          <div
            className="nb-content-loading mx-auto mt-16 max-w-[var(--nb-content-max,820px)] px-5"
            aria-busy="true"
          >
            <div className="nb-loading-pulse" />
          </div>
        </div>
      )}
    </div>
  );
}

export function PublicNotebookPageClient({
  payload,
}: PublicNotebookPageClientProps) {
  const storeKey = `${payload.notebook.id}:${payload.notebook.updated_at ?? "0"}`;

  return (
    <NotebookStoreProvider key={storeKey} notebookId={payload.notebook.id}>
      <NotebookPasteInit />
      <PublicNotebookInner payload={payload} />
    </NotebookStoreProvider>
  );
}
